/**
 * Demand Forecast
 *
 * Predicted shipment volume, computed live from the ERP's own `parcel` data.
 * Admin-facing planning tool — read-only against shipments; the only thing it
 * writes is demand events (festivals, disruptions), which adjust the forecast.
 *
 * Deliberate honesty in the UI: every number ships with a range, and lanes with
 * too little volume for a meaningful daily figure say so rather than printing a
 * confident-looking number the data cannot support.
 */

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import { Autocomplete } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useEffect, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import moment from "moment";

import {
  getForecast, getDemandEvents, addDemandEvent, deleteDemandEvent,
} from "network/service";
import { useLoading } from "context/loading.context";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler
);

const EVENT_TYPES = [
  "festival", "holiday", "carrier_disruption",
  "customs_change", "campaign", "competitor", "other",
];

const emptyEvent = {
  title: "",
  type: "festival",
  scope: "ALL",
  start_date: "",
  end_date: "",
  multiplier: "1.5",
  notes: "",
};

export default function Forecast() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("TOTAL");
  const [granularity, setGranularity] = useState("day");
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [showForm, setShowForm] = useState(false);
  const { showLoader, hideLoader } = useLoading();

  const fetchForecast = useCallback(async () => {
    showLoader();
    setError(null);
    try {
      const resp = await getForecast(`scope=${encodeURIComponent(scope)}&granularity=${granularity}`);
      setData(resp.data);
      if (resp.data && resp.data.error) setError(resp.data.error);
    } catch (err) {
      console.log(err);
      setError("Could not load the forecast. Check that the API is reachable.");
    } finally {
      hideLoader();
    }
  }, [scope, granularity]);

  const fetchEvents = useCallback(async () => {
    try {
      const resp = await getDemandEvents();
      setEvents(resp.data || []);
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const saveEvent = async () => {
    if (!form.title || !form.start_date) return;
    try {
      await addDemandEvent({
        ...form,
        multiplier: parseFloat(form.multiplier) || 1,
        end_date: form.end_date || form.start_date,
        is_active: true,
      });
      setForm(emptyEvent);
      setShowForm(false);
      fetchEvents();
      fetchForecast();
    } catch (err) {
      console.log(err);
    }
  };

  const removeEvent = async (id) => {
    try {
      await deleteDemandEvent(id);
      fetchEvents();
      fetchForecast();
    } catch (err) {
      console.log(err);
    }
  };

  const upcoming = (data && data.upcoming) || [];
  const history = (data && data.history) || [];
  const next = upcoming[0];
  const fmt = (d) =>
    granularity === "week" ? `w/c ${moment(d).format("D MMM")}` : moment(d).format("ddd D MMM");

  // One continuous series: recent actuals, then the forecast with its band.
  const labels = history.slice(-30).map((h) => h.date).concat(upcoming.map((u) => u.date));
  const actualSeries = history.slice(-30).map((h) => h.value).concat(upcoming.map(() => null));
  const pad = history.slice(-30).map(() => null);
  const chartData = {
    labels: labels.map(fmt),
    datasets: [
      {
        label: "Actual",
        data: actualSeries,
        borderColor: "#eb6834",
        backgroundColor: "#eb6834",
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: false,
      },
      {
        label: "Forecast",
        data: pad.concat(upcoming.map((u) => u.predicted)),
        borderColor: "#2a78d6",
        backgroundColor: "#2a78d6",
        borderWidth: 2,
        pointRadius: 2,
      },
      {
        label: "Upper",
        data: pad.concat(upcoming.map((u) => u.upper)),
        borderColor: "rgba(42,120,214,0.25)",
        backgroundColor: "rgba(42,120,214,0.12)",
        borderWidth: 1,
        pointRadius: 0,
        fill: "+1",
      },
      {
        label: "Lower",
        data: pad.concat(upcoming.map((u) => u.lower)),
        borderColor: "rgba(42,120,214,0.25)",
        backgroundColor: "rgba(42,120,214,0.12)",
        borderWidth: 1,
        pointRadius: 0,
        fill: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { labels: { filter: (i) => i.text === "Actual" || i.text === "Forecast" } },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${c.parsed.y}` } },
    },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const tile = (value, label, hint) => (
    <Grid item xs={12} sm={6} lg={3}>
      <Card>
        <MDBox p={2}>
          <MDTypography variant="h4" fontWeight="bold">{value}</MDTypography>
          <MDTypography variant="button" color="text" fontWeight="regular">{label}</MDTypography>
          {hint ? (
            <MDTypography variant="caption" color="text" display="block">{hint}</MDTypography>
          ) : null}
        </MDBox>
      </Card>
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* Header + controls */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">Demand Forecast</MDTypography>
            <MDTypography variant="button" color="text">
              Predicted shipment volume from this system&apos;s own booking history.
              Every figure carries a range — treat the range as the answer, not the point.
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mt={{ xs: 2, md: 0 }}>
            <Autocomplete
              disableClearable
              value={scope}
              options={(data && data.availableScopes) || ["TOTAL"]}
              getOptionLabel={(o) => (o === "TOTAL" ? "All countries" : o)}
              onChange={(e, v) => v && setScope(v)}
              sx={{ width: 220 }}
              renderInput={(params) => <MDInput {...params} label="Scope" />}
            />
            <MDButton
              variant={granularity === "day" ? "gradient" : "outlined"}
              color="info" size="small" onClick={() => setGranularity("day")}
            >
              Daily
            </MDButton>
            <MDButton
              variant={granularity === "week" ? "gradient" : "outlined"}
              color="info" size="small" onClick={() => setGranularity("week")}
            >
              Weekly
            </MDButton>
            <IconButton onClick={fetchForecast}><Icon>refresh</Icon></IconButton>
          </MDBox>
        </MDBox>

        {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

        {/* The honesty warning: small lanes cannot support a daily point figure */}
        {data && granularity === "day" && data.reliableDaily === false ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {scope} averages {data.dailyMean} shipments a day, which is too few for a meaningful
            daily prediction. Read the range, or switch to <b>Weekly</b> for a number worth acting on.
          </Alert>
        ) : null}

        {/* KPIs */}
        <Grid container spacing={2} mb={1}>
          {tile(
            next ? Math.round(next.predicted) : "—",
            `Next ${granularity === "day" ? "day" : "week"}`,
            next ? `range ${Math.round(next.lower)}–${Math.round(next.upper)} · ${fmt(next.date)}` : ""
          )}
          {tile(
            data && data.backtest ? `±${data.backtest.mae}` : "—",
            "Typical error per day",
            data && data.backtest ? `measured over ${data.backtest.testedDays} days` : ""
          )}
          {tile(
            data && data.backtest ? `${data.backtest.wape}%` : "—",
            "Error rate (backtested)",
            "a naive 7-day average scores 47.4%"
          )}
          {tile(
            data && data.model ? data.model.trainDays : "—",
            "Days of history used",
            data && data.model ? `from ${data.model.trainFrom}` : ""
          )}
        </Grid>

        {/* Chart */}
        <Card sx={{ mb: 2 }}>
          <MDBox p={2}>
            <MDTypography variant="h6">Actual vs forecast</MDTypography>
            <MDTypography variant="caption" color="text">
              Shaded band is the predicted range. Data through {data ? data.dataThroughDate : "—"}.
            </MDTypography>
            <MDBox mt={2} sx={{ height: 320 }}>
              {labels.length ? <Line data={chartData} options={chartOptions} /> : null}
            </MDBox>
          </MDBox>
        </Card>

        {/* Upcoming table */}
        <Card sx={{ mb: 2 }}>
          <MDBox p={2}>
            <MDTypography variant="h6">Upcoming</MDTypography>
            <MDTypography variant="caption" color="text">
              Why each number is what it is.
            </MDTypography>
          </MDBox>
          <MDBox px={2} pb={2} sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Forecast</TableCell>
                  <TableCell align="right">Range</TableCell>
                  {granularity === "day" ? <TableCell align="right">Day effect</TableCell> : null}
                  <TableCell>Events applied</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcoming.map((u) => (
                  <TableRow key={u.date}>
                    <TableCell>{fmt(u.date)}</TableCell>
                    <TableCell align="right">
                      <b>{Math.round(u.predicted)}</b>
                    </TableCell>
                    <TableCell align="right">
                      {Math.round(u.lower)}–{Math.round(u.upper)}
                    </TableCell>
                    {granularity === "day" ? (
                      <TableCell align="right">
                        {u.factors && u.factors.dowFactor ? `${u.factors.dowFactor.toFixed(2)}x` : "—"}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      {u.factors && u.factors.eventTitles && u.factors.eventTitles.length
                        ? `${u.factors.eventTitles.join(", ")} (${u.factors.eventMultiplier}x)`
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {upcoming.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <MDTypography variant="button" color="text">No forecast available.</MDTypography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </MDBox>
        </Card>

        {/* Demand events */}
        <Card>
          <MDBox p={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">Demand events</MDTypography>
              <MDButton variant="outlined" color="info" size="small" onClick={() => setShowForm(!showForm)}>
                <Icon>add</Icon>&nbsp;Add
              </MDButton>
            </MDBox>
            <MDTypography variant="caption" color="text">
              Festivals, carrier disruptions and campaigns the model cannot infer by itself.
              A learned festival term was tested and made accuracy worse, so this is where that
              judgement lives. Diwali 2026 is 8 November — worth adding an uplift from around 12 October.
            </MDTypography>

            {showForm ? (
              <MDBox mt={2} p={2} sx={{ backgroundColor: "#f7f7f9", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDInput fullWidth label="Title" value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDInput select fullWidth label="Type" value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}>
                      {EVENT_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>{t.replace(/_/g, " ")}</MenuItem>
                      ))}
                    </MDInput>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDInput select fullWidth label="Scope" value={form.scope}
                      onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                      <MenuItem value="ALL">All countries</MenuItem>
                      {((data && data.availableScopes) || [])
                        .filter((s) => s !== "TOTAL")
                        .map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </MDInput>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDInput fullWidth type="date" label="Start" InputLabelProps={{ shrink: true }}
                      value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDInput fullWidth type="date" label="End" InputLabelProps={{ shrink: true }}
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <MDInput fullWidth type="number" label="Multiplier"
                      inputProps={{ step: 0.1, min: 0.2, max: 4 }}
                      value={form.multiplier}
                      onChange={(e) => setForm({ ...form, multiplier: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text">
                      1.0 = no change · 1.6 = 60% busier · 0.5 = half normal
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDButton fullWidth variant="gradient" color="info" onClick={saveEvent}
                      disabled={!form.title || !form.start_date}>
                      Save
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            ) : null}

            <MDBox mt={2}>
              {events.length === 0 ? (
                <MDTypography variant="button" color="text">No events yet.</MDTypography>
              ) : null}
              {events.map((e) => (
                <MDBox key={e._id}>
                  <Divider sx={{ my: 1 }} />
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium">{e.title}</MDTypography>
                      <MDTypography variant="caption" color="text" ml={1}>
                        {moment(e.start_date).format("D MMM YYYY")}
                        {e.end_date ? ` → ${moment(e.end_date).format("D MMM YYYY")}` : ""}
                        {" · "}{e.scope || "ALL"}{" · "}{e.multiplier}x
                      </MDTypography>
                    </MDBox>
                    <IconButton size="small" onClick={() => removeEvent(e._id)}>
                      <Icon fontSize="small">delete</Icon>
                    </IconButton>
                  </MDBox>
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        </Card>

        <MDBox mt={2}>
          <MDTypography variant="caption" color="text">
            Model: multiplicative seasonal (day-of-week × month × recent level × events),
            backtested on this system&apos;s own history. Volume only — the amount field is
            unreliable across most records, so revenue is deliberately not forecast.
          </MDTypography>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
